import * as React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import ModuleMark from "@/components/identity/ModuleMark";

/**
 * Create a channel OR an announcement channel. Audience can be static (as
 * today) or a computed spec (all | site | role | contract_type |
 * rostered_today). Computed audiences replace the member picker with a
 * label like "Everyone at Vine Lane".
 */
export default function NewChannelDialog({ open, onOpenChange, companyId, onCreated }) {
  const { entitlements } = useCompany();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [siteId, setSiteId] = React.useState("");
  const [kind, setKind] = React.useState("channel");   // channel | announcement
  const [audienceKind, setAudienceKind] = React.useState("static");
  const [audSite, setAudSite] = React.useState("");
  const [audRole, setAudRole] = React.useState("");
  const [audContract, setAudContract] = React.useState("permanent");
  const [sites, setSites] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setName(""); setDescription(""); setSiteId(""); setErr("");
      setKind("channel"); setAudienceKind("static");
      setAudSite(""); setAudRole(""); setAudContract("permanent");
      return;
    }
    Promise.all([
      api.get(`/companies/${companyId}/sites`).catch(() => ({ data: { sites: [] } })),
      api.get(`/companies/${companyId}/roles`).catch(() => ({ data: { roles: [] } })),
    ]).then(([s, r]) => {
      const sitesRows = Array.isArray(s.data?.sites) ? s.data.sites : [];
      const rolesRows = Array.isArray(r.data?.roles) ? r.data.roles : [];
      setSites(sitesRows);
      setRoles(rolesRows);
      if (sitesRows[0]) setAudSite(sitesRows[0].id);
      if (rolesRows[0]) setAudRole(rolesRows[0].id);
    });
  }, [open, companyId]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setErr("");
    setBusy(true);
    const audience_spec = { kind: audienceKind,
      site_id: (audienceKind === "site" || audienceKind === "rostered_today") ? audSite : null,
      role_id: audienceKind === "role" ? audRole : null,
      contract_type: audienceKind === "contract_type" ? audContract : null };
    try {
      const { data } = await api.post(`/companies/${companyId}/comms/channels`, {
        name: name.trim(),
        description: description.trim(),
        site_id: siteId || null,
        kind,
        audience_spec,
      });
      toast.success(`Created #${data.slug}`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(formatApiErrorDetail(d) || "Could not create channel");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/30 flex items-start justify-center pt-20 px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => onOpenChange(false)}
      data-testid="comms-new-channel-dialog"
    >
      <div
        className="w-full max-w-md bg-card rounded-lg shadow-card border border-brand-rule"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-brand-rule flex items-center gap-3">
          <ModuleMark id="comms" size={30} />
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-brand-muted">New channel</div>
            <div className="mt-0.5 text-[15px] font-semibold text-brand-ink">Create channel</div>
          </div>
        </div>
        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <div>
            <label className="text-[10.5px] uppercase tracking-wider text-brand-muted">Kind</label>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {[
                { v: "channel", label: "Discussion", hint: "Two-way, everyone in the audience can post." },
                { v: "announcement", label: "Announcement", hint: "One-way, only comms.manage can post." },
              ].map((o) => (
                <button
                  key={o.v} type="button"
                  className={`text-left rounded-md border p-2.5 ${kind === o.v ? "border-brand-violet bg-[#EDE9FE]" : "border-brand-rule bg-card"}`}
                  onClick={() => setKind(o.v)}
                  data-testid={`comms-new-channel-kind-${o.v}`}
                >
                  <div className="text-[12.5px] font-semibold text-brand-ink">{o.label}</div>
                  <div className="text-[10.5px] text-brand-muted">{o.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-wider text-brand-muted">Name</label>
            <input
              className="field-input mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === "announcement" ? "operations-notices" : "ops-questions"}
              autoFocus
              maxLength={48}
              required
              data-testid="comms-new-channel-name"
            />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-wider text-brand-muted">Description</label>
            <input
              className="field-input mt-1"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this channel for?"
              maxLength={280}
              data-testid="comms-new-channel-desc"
            />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-wider text-brand-muted">Audience</label>
            <select
              className="field-input mt-1"
              value={audienceKind}
              onChange={(e) => setAudienceKind(e.target.value)}
              data-testid="comms-new-channel-audience-kind"
            >
              <option value="static">Managed member list (add people manually)</option>
              <option value="all">Everyone in the company</option>
              <option value="site">Everyone at a site</option>
              <option value="role">Everyone with a role</option>
              <option value="contract_type">Everyone on a contract type</option>
              <option value="rostered_today">Everyone rostered right now at a site</option>
            </select>
            {(audienceKind === "site" || audienceKind === "rostered_today") && (
              <select
                className="field-input mt-1.5"
                value={audSite}
                onChange={(e) => setAudSite(e.target.value)}
                data-testid="comms-new-channel-audience-site"
              >
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {audienceKind === "role" && (
              <select
                className="field-input mt-1.5"
                value={audRole}
                onChange={(e) => setAudRole(e.target.value)}
                data-testid="comms-new-channel-audience-role"
              >
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            {audienceKind === "contract_type" && (
              <select
                className="field-input mt-1.5"
                value={audContract}
                onChange={(e) => setAudContract(e.target.value)}
                data-testid="comms-new-channel-audience-contract"
              >
                <option value="permanent">Permanent</option>
                <option value="fixed_term">Fixed term</option>
                <option value="zero_hours">Zero hours</option>
                <option value="casual">Casual</option>
              </select>
            )}
            <div className="mt-1 text-[10.5px] text-brand-muted">
              {audienceKind === "static"
                ? "You'll add people one by one from the channel drawer."
                : "Membership updates automatically on hire, transfer, or roster change."}
            </div>
          </div>
          {err && (
            <div className="text-[12px] text-brand-coral" data-testid="comms-new-channel-error">{err}</div>
          )}
          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn-violet flex-1" disabled={busy || !name.trim()}
                    data-testid="comms-new-channel-submit">
              {busy ? "…" : "Create channel"}
            </button>
            <button type="button" onClick={() => onOpenChange(false)} className="btn-outline"
                    data-testid="comms-new-channel-cancel">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
